import { JSX, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { MAX_QIT_IDS_FROM_PARAMS } from 'src/redux/slices/Interview/types';
import { useSurvey } from 'src/contexts/SurveyContext';
import InterviewShell from './InterviewShell';

/**
 * SurveyInterviewRoute — boots the interview engine for a re-answer flow
 * launched from the Survey Explorer ("Something doesn't look right").
 *
 * Handles two entry points (with and without memberId in URL):
 * - /renter/survey/:surveyId/sections/:sectionId/answer
 * - /renter/survey/:surveyId/members/:memberId/sections/:sectionId/answer
 *
 * The ?qits=1,2,3 query param limits the interview to specific questions.
 * surveyId is taken from the URL param (not from context) since it is always
 * present in the Survey Explorer URL.
 * actingOnMemberId comes from SurveyContext (set by SurveyLayout).
 */
export default function SurveyInterviewRoute(): JSX.Element | null {
  const { surveyId, sectionId, memberId } = useParams<{
    surveyId: string;
    sectionId: string;
    memberId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const { actingOnMemberId } = useSurvey();
  const dispatch = useDispatch();
  const { callIPE } = useIPE();

  const parsedSurveyId = surveyId ? parseInt(surveyId, 10) : null;

  // Parse ?qits=1,2,3 into an array of numbers (capped for safety)
  const qitsParam = searchParams.get('qits');
  const qitIds: number[] = qitsParam
    ? qitsParam
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n))
        .slice(0, MAX_QIT_IDS_FROM_PARAMS)
    : [];

  // Parse ?answer_group_qits=100:111-222,200:444 into {ag_id: [qit_ids]}
  const agParam = searchParams.get('answer_group_qits');
  const answerGroupQits: Record<number, number[]> = {};
  agParam?.split(',').forEach((chunk) => {
    const trimmed = chunk.trim();
    if (trimmed.includes(':')) {
      const [agStr, qitsStr] = trimmed.split(':');
      const agId = parseInt(agStr, 10);
      if (!isNaN(agId)) {
        const qitIds = qitsStr
          .split('-')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !isNaN(n));
        if (qitIds.length > 0) {
          answerGroupQits[agId] = qitIds;
        }
      }
    }
  });

  // Merge qits and answer_group_qits QITs into a single onlyQitIds list so
  // the interview engine knows this is a scoped session (suppresses non-
  // question work items, drives exhaustion/advance logic).
  const allQitIdsSet = new Set(qitIds);
  for (const qitIds of Object.values(answerGroupQits)) {
    for (const id of qitIds) {
      allQitIdsSet.add(id);
    }
  }
  const onlyQitIds = allQitIdsSet.size > 0 ? Array.from(allQitIdsSet) : [];

  // Build the back path so the shell's back button returns to the section page
  const backPath = memberId
    ? `/renter/survey/${surveyId}/members/${memberId}${
        sectionId ? `/sections/${sectionId}` : ''
      }`
    : `/renter/survey/${surveyId}${sectionId ? `/sections/${sectionId}` : ''}`;

  useEffect(() => {
    if (!parsedSurveyId) {
      return;
    }
    if (process.env.NODE_ENV !== 'production') {
      console.log('[SurveyInterviewRoute] booting scoped interview', {
        parsedSurveyId,
        onlyQitIds,
        answerGroupQits,
        backPath,
      });
    }
    dispatch(
      interviewActions.initInterview({
        surveyId: parsedSurveyId,
        onBehalfOf: actingOnMemberId,
        onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : null,
        answerGroupQits:
          Object.keys(answerGroupQits).length > 0 ? answerGroupQits : null,
        backPath,
      })
    );
    callIPE({
      surveyIdOverride: parsedSurveyId,
      onBehalfOfOverride: actingOnMemberId,
      onlyQitIds: onlyQitIds.length > 0 ? onlyQitIds : undefined,
      answerGroupQits:
        Object.keys(answerGroupQits).length > 0 ? answerGroupQits : undefined,
    });
    return () => {
      dispatch(interviewActions.resetInterview());
    };
    // parsedSurveyId is the boot trigger. actingOnMemberId, onlyQitIds, and
    // backPath are stable for the lifetime of this route.
  }, [parsedSurveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!parsedSurveyId) {
    return null;
  }

  return <InterviewShell />;
}
